import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';
import { translateFn } from 'apps/webcomponents/src/utils/translator.helper';
import { LitElement, html, TemplateResult } from 'lit';
import { property, customElement, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { BreachPathDetailsResourceSideMenuDataType } from '../types';
import { BreachPathEvents } from '../../common/types'
import {
  resourceNameRenderer,
  riskScoreRenderer,
  lastScannedOnRenderer,
  resourceIdRenderer,
  resourceTypeTextRenderer,
  resourceTypeRenderer,
  platformRenderer,
  regionRenderer,
  accountIdRenderer,
  networkInterfaceIdRenderer,
  subnetIdRenderer,
  virtualPrivateCloudIdRenderer,
  headerIconRenderer,
} from '../utils/customDataRenderer';
import { AffectedDeviceType } from 'apps/webcomponents/src/riskManagement/common/constants';
import { BreachPathNodeRisk, CveData } from '../../breachPathDetails/types';
import { ConsoleRequest, DataProviderType } from 'apps/webcomponents/src/utils/request';
import { CVE_SIDE_PANEL_SHOW_LIMIT, CVE_SIDE_PANEL_SHOW_INCREMENT } from 'apps/webcomponents/src/riskManagement/common/constants';

/**
 * @summary Breach Path details resource side menu web component
 * @tag breach-path-details-resource-side-menu
 * Here https://custom-elements-manifest.open-wc.org/analyzer/getting-started/ you can find out how to document the code
 */
@customElement('breach-path-details-resource-side-menu')
export class BreachPathDetailsResourceSideMenuWebComponent extends LitElement {
  @property({ type: Object, attribute: false })
  data: BreachPathDetailsResourceSideMenuDataType = {} as BreachPathDetailsResourceSideMenuDataType;

  @property({ type: Boolean, attribute: false })
  loading = false;

  @state()
  private shouldShowViewResourceButton: boolean;

  @state()
  private cveData: CveData[];

  static get styles() {
    return styles;
  }

  updated(changedProps: Map<string, unknown>) {
    super.updated(changedProps);

    if (changedProps.has('data')) {
      this.getShouldShowViewResourceButton();
      this.getCveData();
    }
  }

  render(): TemplateResult {
    if (this.loading) {
      return this.renderLoading();
    }

    return html`
      <div class="header mt-6">
        ${this.renderHeaderSection()}
      </div>
      <div class="content">
        ${this.renderGeneralSection()}
        ${this.renderCloudAccountSection()}
        ${this.renderInvolvedPublicRisksSection()}
      </div>
    `;
  }

  private renderLoading(): TemplateResult {
    return html`
      <gz-loader rows="5" style="padding-right: 16px; padding-top: 48px;"></gz-loader>
    `;
  }

  private renderHeaderSection(): TemplateResult {
    return html`
      <div class="header-wrapper">
        <div>
          ${this.renderHeaderIcon()}
        </div>
        <div class="flex-1">
          <div class="d-flex align-items-center">
            <gz-title fontSize="${FontSizes.HEADING_5}">
              ${resourceNameRenderer(this.data)}
            </gz-title>
            <gz-copy-to-clipboard
              class="copy-icon"
              confirmationMessage="${translateFn('Copied')}"
              tooltipText="${translateFn('Copy to clipboard')}"
              clipboardMessage="${resourceNameRenderer(this.data)}"
            ></gz-copy-to-clipboard>
          </div>
          <gz-text
            class="header-type"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
          >
            ${resourceTypeTextRenderer(this.data)}
          </gz-text>
        </div>
      </div>
    `;
  }

  private renderGeneralSection(): TemplateResult {
    return html`
      <div class="section pb-1" data-section="general">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn('General')}
        </gz-title>
        <gz-labeled-content label="${translateFn('Risk score')}">
          ${riskScoreRenderer(this.data)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Resource type')}">
          ${resourceTypeRenderer(this.data)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Resource ID')}">
          ${this.renderLabeledContentWithEllipsisCopy(resourceIdRenderer(this.data))}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Platform')}">
          ${platformRenderer(this.data)}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Last scanned on')}">
          ${lastScannedOnRenderer(this.data)}
        </gz-labeled-content>
      </div>
    `;
  }

  private renderRelatedResourcesSection(): TemplateResult | null {
    if (this.data.type === AffectedDeviceType.EC2_NETWORK_ACL) {
      return html`
        <div class="section pt-2 pb-1" data-section="related-resources">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="section-title text-uppercase mb-2"
          >
            ${translateFn('Related resources')}
          </gz-title>
          <gz-labeled-content label="${translateFn('Subnet ID')}">
            ${this.renderLabeledContentWithEllipsisCopy(subnetIdRenderer(this.data))}
          </gz-labeled-content>
          <gz-labeled-content label="${translateFn('Virtual Private Cloud ID')}">
            ${this.renderLabeledContentWithEllipsisCopy(virtualPrivateCloudIdRenderer(this.data))}
          </gz-labeled-content>
        </div>
      `;
    }

    if (this.data.type === AffectedDeviceType.NETWORK_NETWORK_SECURITY_GROUPS) {
      return html`
        <div class="section pt-2 pb-1" data-section="related-resources">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="section-title text-uppercase mb-2"
          >
            ${translateFn('Related resources')}
          </gz-title>
          <gz-labeled-content label="${translateFn('Network Interface ID')}">
            ${this.renderLabeledContentWithEllipsisCopy(networkInterfaceIdRenderer(this.data))}
          </gz-labeled-content>
          <gz-labeled-content label="${translateFn('Subnet ID')}">
            ${this.renderLabeledContentWithEllipsisCopy(subnetIdRenderer(this.data))}
          </gz-labeled-content>
          <gz-labeled-content label="${translateFn('Virtual Private Cloud ID')}">
            ${this.renderLabeledContentWithEllipsisCopy(virtualPrivateCloudIdRenderer(this.data))}
          </gz-labeled-content>
        </div>
      `;
    }

    return null;
  }

  private renderCloudAccountSection(): TemplateResult {
    return html`
      <div class="section py-2" data-section="cloud-account">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn('Cloud account')}
        </gz-title>
        <gz-labeled-content label="${translateFn('Account ID')}">
          ${this.renderLabeledContentWithEllipsisCopy(accountIdRenderer(this.data))}
        </gz-labeled-content>
        <gz-labeled-content label="${translateFn('Region')}">
          ${regionRenderer(this.data)}
        </gz-labeled-content>
        <div class="sidemenu-actions-wrapper">
          ${this.renderActionButtons()}
        </div>
      </div>
    `;
  }

  private renderActionButtons(): TemplateResult {
    if (this.shouldShowViewResourceButton === undefined) return html`<gz-loader rows="1"></gz-loader>`;

    // View resource button
    const viewResourceButton = this.shouldShowViewResourceButton ? html`
      <gz-button
        type="outline"
        lowercase="true"
        buttonHeight="small"
        buttonfontSize="small"
        @click="${this.handleViewResourceButtonClick}"
      >
        ${translateFn('View resource')}
      </gz-button>
    ` : null;

    // View metadata button
    let hasMetadata = false;
    try {
      JSON.parse(this.data.metadata);
      hasMetadata = true
    } catch {}
    const viewMetadataButton = hasMetadata ? html`
      <gz-button
        type="outline"
        lowercase="true"
        buttonHeight="small"
        buttonfontSize="small"
        @click="${this.handleViewMetadataButtonClick}"
      >
        ${translateFn('View metadata')}
      </gz-button>
    `: null;

    if (this.shouldShowViewResourceButton) {
      return html`
        ${viewResourceButton}
        ${viewMetadataButton}
      `
    }

    return html`${viewMetadataButton}`
  }

  private renderPublicRisksSection(): TemplateResult {
    return html`
      <div class="section py-2" data-section="public-risks">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn(`Involved public facing risks (${1})`)}
        </gz-title>
        <gz-accordion
          accordionTitle="${'Public EC2 Subnet'}"
          align="left"
        >
          <div class="pb-2 pt-1">
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${'A short explanation goes here port is used for the ssh server. Attackers can run malicious programs using sudo, causing it to fork a background process that persists.'}
            </gz-text>
            <div class="pt-2">
              <gz-labeled-content label="${translateFn('Subnet status')}">
                ${'Open'}
              </gz-labeled-content>
              <gz-labeled-content label="${translateFn('Risk mitigation')}">
              </gz-labeled-content>
              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
              >
                ${'Lorem ipsum dolor sit amet, consectetur adipiscing elit'}
              </gz-text>
            </div>
          </div>
        </gz-accordion>
        <gz-accordion
          accordionTitle="${'Allow all traffic by Network ACL'}"
          align="left"
        >
          <div class="pb-2 pt-1">
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${'A Network ACL associated with Subnet allows all traffic.'}
            </gz-text>
            <div class="pt-2">
              <gz-labeled-content label="${translateFn('Risk mitigation')}">
              </gz-labeled-content>
              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
              >
                ${'Lorem ipsum dolor sit amet, consectetur adipiscing elit'}
              </gz-text>
            </div>
          </div>
        </gz-accordion>
        <gz-accordion
          accordionTitle="${'Port 22 is allowed by Security Group'}"
          align="left"
        >
          <div class="pb-2 pt-1">
            <gz-text
              fontSize="${FontSizes.PARAGRAPH_12}"
              fontWeight="${FontWeights.NORMAL}"
            >
              ${'A Security Group attached to Network Interface has public facing port 22.'}
            </gz-text>
            <div class="pt-2">
              <gz-labeled-content label="${translateFn('Risk mitigation')}">
              </gz-labeled-content>
              <gz-text
                fontSize="${FontSizes.PARAGRAPH_12}"
                fontWeight="${FontWeights.NORMAL}"
              >
                ${'Lorem ipsum dolor sit amet, consectetur adipiscing elit'}
              </gz-text>
            </div>
          </div>
        </gz-accordion>
      </div>
    `;
  }

  private renderInvolvedPublicRisksSection(): TemplateResult | null {
    if (this.cveData === undefined) {
      return html`
        <div class="section py-2" data-section="public-risks">
          <gz-title
            fontSize="${FontSizes.PARAGRAPH_11}"
            fontWeight="${FontWeights.SEMIBOLD}"
            class="section-title text-uppercase mb-2"
          >
            ${translateFn(`Involved vulnerabilities`)}
          </gz-title>
          <gz-loader rows="5"></gz-loader>
        </div>
      `
    };

    if (this.cveData.length === 0) {
      return null;
    }

    return html`
      <div class="section py-2" data-section="public-risks">
        <gz-title
          fontSize="${FontSizes.PARAGRAPH_11}"
          fontWeight="${FontWeights.SEMIBOLD}"
          class="section-title text-uppercase mb-2"
        >
          ${translateFn(`Involved vulnerabilities (${this.cveData.length})`)}
        </gz-title>
        <gz-application-cve-list
          .cveList="${this.cveData}"
          .companyId="${this.data.companyId}"
          .hasViewDevicesButtonVisible="${true}"
          .isShowMorePresent="${true}"
          .showMoreLimit="${CVE_SIDE_PANEL_SHOW_LIMIT}"
          .increment="${CVE_SIDE_PANEL_SHOW_INCREMENT}"
          .isRenderSectionTitle="${false}"
        >
        </gz-application-cve-list>
      </div>
    `;
  }

  private renderHeaderIcon(): TemplateResult {
    return headerIconRenderer(this.data);
  }

  private renderLabeledContentWithEllipsisCopy(value: string): TemplateResult {
    return html`
      <div class="w-full d-flex align-items-center copy-labeled-content">
        <gz-tooltip
          class="flex-1"
          isEllipsis
          tooltipText="${value}"
        >
          <span>${value}</span>
        </gz-tooltip>
        <gz-copy-to-clipboard
          class="copy-icon"
          confirmationMessage="${translateFn('Copied')}"
          tooltipText="${translateFn('Copy to clipboard')}"
          clipboardMessage="${value}"
        ></gz-copy-to-clipboard>
      </div>
    `;
  }

  private handleViewResourceButtonClick = () => {
    const [companyId, resourceId, operatingSystem] = this.getResourceButtonData();

    this.dispatchEvent(new CustomEvent(BreachPathEvents.NAV_VIEW_RESOURCES_PAGE, {
      detail: {
        companyId,
        resourceId: {
          label: resourceId,
          value: resourceId,
        },
        operatingSystem: parseInt(operatingSystem),
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    }));
  }

  private handleViewMetadataButtonClick = () => {
    this.dispatchEvent(new CustomEvent(BreachPathEvents.VIEW_METADATA_RESOURCES_SIDEMENU, {
      detail: {
        data: this.data,
      },
      bubbles: true,
      cancelable: true,
      composed: true,
    }));
  }

  private getShouldShowViewResourceButton = async () => {
    const [companyId, resourceId, operatingSystem] = this.getResourceButtonData();

    if (!companyId || !resourceId || !operatingSystem) {
      this.shouldShowViewResourceButton = false;
      return;
    };

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'RiskManagementDevices',
      method: 'readRecords',
      payload: {
        page: 1,
        limit: 1,
        filter: [
          { property: "companyId[exactMatch]", value: companyId },
          { property: "operatingSystem[exactMatch]", value: parseInt(operatingSystem) },
          { property: "resourceId[exactMatch]", value: resourceId },
        ],
      },
    });

    this.shouldShowViewResourceButton = !!response?.data?.length;
  }

  private getResourceButtonData = () => {
    const companyId = this.data.companyId;
    const [, resourceId, operatingSystem] = this.data.uniqueIdentifier?.split('|') ?? [];

    return [companyId, resourceId, operatingSystem];
  }

  private getCveData = async () => {
    let metadata: BreachPathNodeRisk = {};

    try {
      metadata = JSON.parse(this.data.risk);
    } catch {}

    if (!metadata?.information || metadata.information.cve_ids.length === 0) {
      this.cveData = [];
      return;
    }

    const response = await ConsoleRequest.createRequest<any>({
      dataProviderType: DataProviderType.ENDPOINTS,
      endpoint: 'GlobalInventoryCve',
      method: 'getCveDataByStandardIds',
      payload: {
        cvesStandardIds: metadata.information.cve_ids,
      },
    });

    this.cveData = response;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'breach-path-details-resource-side-menu': BreachPathDetailsResourceSideMenuWebComponent;
  }
}
